import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { DM_LIST_PAGE, type DmLastMessage } from "@bing/shared";
import type { FriendView } from "../api/client";
import { Avatar } from "../components/Avatar";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FriendPickerSheet } from "../components/FriendPickerSheet";
import { displayName } from "../components/profile-ui";
import { ChatComposer, ChatConnectionNotice, ChatMessageList } from "../components/ChatPanel";
import { GameIcon } from "../components/GameIcon";
import { ScreenTitle } from "../components/ScreenTitle";
import { OfficialBadge, Spinner } from "../components/social-ui";
import { GAME_SERVER_URL } from "../config";
import { createChatStore } from "../game/chat-store";
import {
  conversationId,
  createColyseusDmConnector,
  DM_BLOCKED_CODE,
  type DmMessage,
} from "../game/dm";
import type { VoiceRef } from "../game/voice-note";
import { faDigits } from "../i18n/digits";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/** Subtle felt-texture wash from the Stitch design (main container `felt-texture`). */
const FELT =
  "radial-gradient(circle at 50% 0%, rgb(var(--c-primary-container) / 0.1) 0%, transparent 70%)";

/**
 * Screen-local Persian strings the shared `fa` dictionary doesn't cover yet (this
 * redesign only touches the chat surfaces). Fold into `fa.chat` when it grows.
 */
const t = {
  compose: "گفتگوی جدید",
  search: "جستجوی گفتگوها",
  searchPlaceholder: "جستجوی دوستان…",
  /** Shown when the user has friends but no ACTUAL conversation yet (default list empty). */
  noConversations: "هنوز گفتگویی نداری",
  noConversationsHint: "برای شروع، اسم دوستت رو جستجو کن یا روی ✎ بزن",
  /** Header name/avatar tap → the peer's profile screen. */
  openProfile: "نمایش پروفایل کاربر",
} as const;

/* Peer names come from the shared `displayName` (`components/profile-ui`) so a
 * friend reads identically in chat and in the friends list: nickname → Telegram
 * name → handle → placeholder. No `@handle` sublines here — handles render only
 * where users search BY handle (the Friends add tab). */

/** Inline `currentColor` SVGs standing in for the Stitch Material Symbols. */
function ComposeIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
      <path d="M4 17.25V20h2.75L16.8 9.94l-2.75-2.75L4 17.25zm15.7-10.2a.75.75 0 0 0 0-1.06l-1.69-1.69a.75.75 0 0 0-1.06 0l-1.32 1.32 2.75 2.75 1.32-1.32z" />
    </svg>
  );
}
function SearchIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.2-3.2" />
    </svg>
  );
}
function GroupAddIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.5 18.5c0-2.9 2.9-4.6 6.5-4.6c1.2 0 2.3.2 3.2.5c-.8.9-1.2 2-1.2 3.2v1.6H2.5v-.7z" />
      <path d="M18 8h1.8v2.2H22V12h-2.2v2.2H18V12h-2.2v-1.8H18V8z" />
    </svg>
  );
}
/** Slashed circle — the block glyph (header action, blocked banner, list rows). */
function BlockIcon({ size = 15 }: { size?: number }): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.9 7.9 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.9 7.9 0 0 1 20 12c0 4.42-3.58 8-8 8z" />
    </svg>
  );
}

/** The one-line preview under a conversation's name: the LAST message when the
 * thread has any («شما:»-prefixed for own messages, «🎤 پیام صوتی» for voice),
 * else the tap-to-chat placeholder. */
export function previewLine(preview: DmLastMessage | null | undefined): string {
  if (!preview) return fa.chat.tapToChat;
  const body = preview.kind === "voice" ? fa.chat.voicePreview : preview.body;
  return preview.fromMe ? `${fa.chat.youPrefix}: ${body}` : body;
}

/** Trash glyph for the row's «حذف گفتگو» affordance (inline `currentColor`). */
function TrashIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

/**
 * A conversation card (Stitch "Chat Item"): avatar, name + last-message
 * preview (voice notes render «🎤 پیام صوتی», not their empty body), an
 * unread-count badge when messages are waiting, open chevron, and a small
 * trash button for the one-sided «حذف گفتگو». The card is a clickable row
 * (`role="button"`) that opens the DM thread; the trash button stops
 * propagation so it deletes instead of opening. A peer the user has BLOCKED
 * renders muted with a small slashed-circle glyph and the «مسدود شده» hint.
 */
function ConversationRow({
  user,
  unread = 0,
  preview = null,
  onOpen,
  onDelete,
}: {
  user: FriendView;
  /** Unread DMs from this friend (0 = no badge). */
  unread?: number;
  /** Newest message in this thread (null = no conversation yet). */
  preview?: DmLastMessage | null;
  onOpen: () => void;
  /** Tap the trash affordance → open the delete-conversation confirm. */
  onDelete: () => void;
}): ReactNode {
  const blocked = user.status === "blocked";
  return (
    // A clickable DIV (not a <button>) so the trash <button> can nest inside it
    // and stop the open-chat tap; keyboard-activatable (Enter/Space) for a11y.
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl border border-outline-variant/50 bg-surface-container px-3 py-2.5 text-right transition-all hover:bg-surface-container-high active:scale-[0.99]"
      style={{ boxShadow: "0 2px 4px -1px rgba(0,0,0,0.25)" }}
    >
      <Avatar
        avatarId={user.avatarId}
        avatarPhotoId={user.avatarPhotoId}
        size={44}
        ringClass="border-outline"
        ringWidth={1}
      />
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate font-body-md text-body-md font-semibold ${blocked ? "text-on-surface-variant" : "text-on-surface"}`}
        >
          {displayName(user)}
        </span>
        <span
          className={`mt-0.5 block truncate font-caption text-caption ${
            !blocked && unread > 0 ? "font-semibold text-on-surface" : "text-on-surface-variant"
          }`}
        >
          {blocked ? fa.chat.blockedPeer : previewLine(preview)}
        </span>
      </span>
      {unread > 0 && !blocked ? (
        <span
          aria-label={fa.chat.unread(faDigits(unread))}
          className="flex h-6 min-w-[24px] flex-none items-center justify-center rounded-full bg-error px-1.5 font-label-md text-label-md font-bold text-on-error shadow"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {faDigits(unread)}
        </span>
      ) : null}
      {blocked ? (
        /* Muted blocked glyph in place of the chat chevron. */
        <span
          role="img"
          aria-label={fa.chat.blockedPeer}
          title={fa.chat.blockedPeer}
          className="flex-none text-outline"
        >
          <BlockIcon size={18} />
        </span>
      ) : (
        <span className="flex-none text-secondary transition-transform group-active:scale-90">
          <GameIcon type="chat" size={20} />
        </span>
      )}
      <button
        type="button"
        aria-label={fa.chat.deleteThread}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="flex h-8 w-8 flex-none cursor-pointer appearance-none items-center justify-center rounded-full border-0 bg-transparent p-0 text-on-surface-variant transition-colors hover:text-error active:scale-90"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

/**
 * Presentational DM thread — the design's `chatPanel()`: an avatar top bar over the
 * SHARED chat UI (`ChatMessageList` bubbles + `ChatComposer` with quick emotes,
 * text send and the voice-note mic). Pure props, unit-testable without a WebSocket.
 *
 * `onToggleBlock` (when provided) renders the compact header block control —
 * an icon pill («مسدود کردن» + slashed circle) while the peer is NOT blocked.
 * While `blockedByMe` the header pill yields to an in-thread BANNER (block
 * glyph + «این کاربر رو مسدود کردی» + the inline «آنبلاک» action — one clear
 * place to undo) and the whole composer is disabled; unblocking restores
 * everything. (The confirm dialog lives in the container.)
 */
export function ChatThreadView({
  title,
  official = false,
  avatarId,
  avatarPhotoId,
  selfId,
  messages,
  connecting,
  reconnecting = false,
  onSend,
  onSendVoice,
  onBack,
  onOpenProfile,
  blockedByMe = false,
  onToggleBlock,
  blockBusy = false,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
}: {
  title: string;
  /** Official/verified peer → a badge next to the header name. */
  official?: boolean;
  avatarId?: string;
  avatarPhotoId?: string | null;
  selfId: string;
  messages: readonly DmMessage[];
  connecting: boolean;
  /** Older pages exist / one is in flight / ask for the next — see the api's dm history route. */
  hasMore?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: (() => void) | undefined;
  /** The lifecycle is re-establishing the dropped chat socket: a slim inline
   * notice under the header + a muted composer (sends can't be delivered, so
   * they are refused-by-disable rather than silently dropped). */
  reconnecting?: boolean;
  onSend: (body: string) => void;
  onSendVoice?: (ref: VoiceRef) => void;
  onBack: () => void;
  /** Tap the header avatar/name → open this peer's profile screen (omit → not tappable). */
  onOpenProfile?: () => void;
  /** Whether the CALLER has blocked this peer (drives the آنبلاک control + mute). */
  blockedByMe?: boolean;
  /** Tap on the header block control (block → confirm flow / unblock directly). */
  onToggleBlock?: () => void;
  /** Disables the block control while a block/unblock request is in flight. */
  blockBusy?: boolean;
}): ReactNode {
  return (
    <div className="flex h-full flex-col bg-surface" style={{ backgroundImage: FELT }} dir="rtl">
      <header
        className="flex flex-none items-center gap-3 border-b border-outline-variant/30 bg-surface/90 px-md"
        style={{
          minHeight: 64,
          // NO own top inset: the App shell already pads every tab screen by
          // max(env(safe-area-inset-top), --tg-safe-top) (App.tsx). Adding
          // env() here again double-counted the notch inset → the excess top
          // gap on iPhone (Chat-specific, since only this header self-padded).
          backdropFilter: "blur(12px)",
        }}
      >
        <button
          type="button"
          aria-label={fa.chat.back}
          onClick={onBack}
          className="flex h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-full border border-outline-variant bg-surface-container-high text-secondary transition-transform hover:bg-surface-bright/20 active:scale-95"
        >
          <GameIcon type="back" size={18} />
        </button>
        {/* Avatar + name open the peer's profile (with `onOpenProfile`); back from the profile
            returns to THIS thread (the store keeps `chatPeer` + remembers the chat as the origin). */}
        <button
          type="button"
          onClick={onOpenProfile}
          disabled={!onOpenProfile}
          aria-label={t.openProfile}
          className="flex min-w-0 flex-1 cursor-pointer appearance-none items-center gap-3 border-0 bg-transparent p-0 text-right transition-opacity active:opacity-80 disabled:cursor-default disabled:active:opacity-100"
        >
          <Avatar
            avatarId={avatarId ?? "fox"}
            avatarPhotoId={avatarPhotoId}
            size={40}
            ringClass="border-secondary/70"
          />
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="m-0 truncate font-headline-sm text-headline-sm text-on-surface">
              {title}
            </span>
            {official ? <OfficialBadge size={16} /> : null}
          </span>
        </button>
        {onToggleBlock && !blockedByMe ? (
          <button
            type="button"
            aria-label={fa.chat.block}
            disabled={blockBusy}
            onClick={onToggleBlock}
            className="flex flex-none cursor-pointer appearance-none items-center gap-1.5 rounded-full border border-outline-variant bg-surface-container-high px-3 py-1.5 font-caption text-caption text-on-surface-variant transition-all hover:text-error active:scale-95 disabled:cursor-default disabled:opacity-50"
          >
            <BlockIcon size={13} />
            <span>{fa.chat.block}</span>
          </button>
        ) : null}
      </header>

      {reconnecting ? <ChatConnectionNotice status="reconnecting" /> : null}
      {/* Retention policy, stated where the history lives: only the last 30 days survive. */}
      <p className="m-0 flex-none px-4 pt-1.5 text-center font-caption text-[11px] leading-4 text-on-surface-variant/80">
        {fa.chat.retentionNotice}
      </p>
      <ChatMessageList
        messages={messages}
        selfId={selfId}
        connecting={connecting}
        hasMore={hasMore}
        loadingOlder={loadingOlder}
        onLoadOlder={onLoadOlder}
      />
      {blockedByMe ? (
        /* ⚠️ THIS REPLACES THE MESSAGE BOX — it does not sit above a greyed-out one.
         *
         * A disabled composer reads as «the box is here and something is wrong with it», and on a
         * blocked thread nothing is wrong: it is open, the history is all there, and the only
         * thing missing is the ability to write — which has one cause and one cure, both said
         * here. It is also the whole of what the BLOCKER sees and the blocked person does not, so
         * it carries the state alone rather than sharing it with a pill.
         *
         * One line at 320px (32px glyph + gaps + a short sentence + the action); the action is
         * `shrink-0` and the sentence wraps beside it. */
        <div
          role="status"
          className="mx-4 mb-1 mt-2 flex flex-none items-center gap-2.5 rounded-xl border border-error/30 bg-error-container/25 px-3 py-2"
          style={{ animation: "bg-pop 0.25s ease" }}
        >
          <span
            aria-hidden="true"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-error/15 text-error"
          >
            <BlockIcon size={16} />
          </span>
          <p className="m-0 min-w-0 flex-1 text-right font-caption text-caption text-on-surface">
            {fa.chat.blockedNotice}
          </p>
          {onToggleBlock ? (
            <button
              type="button"
              disabled={blockBusy}
              onClick={onToggleBlock}
              className="shrink-0 cursor-pointer appearance-none rounded-full border border-secondary/60 bg-secondary/15 px-3 py-1.5 font-caption text-caption font-bold text-secondary transition-transform active:scale-95 disabled:cursor-default disabled:opacity-50"
            >
              {fa.chat.blockedUnblock}
            </button>
          ) : null}
        </div>
      ) : (
        <ChatComposer
          onSend={onSend}
          onSendVoice={onSendVoice}
          disabled={reconnecting}
          // The app's in-flow BottomNav sits directly beneath this thread and ALREADY reserves
          // `8px + max(env(safe-area-inset-bottom), --tg-safe-bottom)`. Reserving it here as well
          // was the reported gap: dead space of 17px + that inset, worse on Android where the
          // inset also carries Telegram's own chrome. The nav keeps the reservation; we drop ours.
          bottomInset="none"
        />
      )}
    </div>
  );
}

/**
 * Container: owns the DM connection + store for a single peer, plus the
 * block/unblock flow. The peer's relationship state comes from the SESSION
 * store's `chatPeer` `FriendView` (it already carries `status`/`direction`,
 * set when the thread was opened from a friends/chat list) and is refreshed
 * from every block/unblock mutation's returned view — no extra endpoint.
 */
function ChatThread({ peer, selfId }: { peer: FriendView; selfId: string }): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const setChatPeer = useSession((s) => s.setChatPeer);
  const openUserProfile = useSession((s) => s.openUserProfile);
  const blockUser = useSession((s) => s.blockUser);
  const unblockUser = useSession((s) => s.unblockUser);
  const api = useSession((s) => s.api);

  // Block flow state: the pending confirm dialogs, in-flight guard, failure toast.
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  /** ⚠️ UNBLOCKING CONFIRMS TOO NOW, through the SAME dialog «کاربران مسدود» uses. It used to fire
   *  straight off the tap, so the app asked before blocking and not before undoing it — and that
   *  control now sits where the SEND BUTTON was, which is the one place a mis-tap is most likely.
   *  One copy of the question, two screens. */
  const [confirmingUnblock, setConfirmingUnblock] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);

  const store = useMemo(
    () =>
      createChatStore(
        createColyseusDmConnector({
          wsUrl: GAME_SERVER_URL,
          conversationId: conversationId(selfId, peer.userId),
          getInitData: () => initData,
        }),
        {
          selfId, // so a LIVE incoming line chimes — and our own echo never does
          // The thread paints from HTTP; the socket joins behind it and carries live traffic.
          loadHistory: (before) => api.getDmHistory(peer.userId, before),
        },
      ),
    [selfId, peer.userId, initData],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const error = useStore(store, (s) => s.error);
  const messages = useStore(store, (s) => s.messages);
  const send = useStore(store, (s) => s.send);
  const sendVoice = useStore(store, (s) => s.sendVoice);
  const retry = useStore(store, (s) => s.retry);
  const hasMore = useStore(store, (s) => s.hasMore);
  const loadingOlder = useStore(store, (s) => s.loadingOlder);
  const loadingHistory = useStore(store, (s) => s.loadingHistory);
  const loadOlder = useStore(store, (s) => s.loadOlder);

  const blockedByMe = peer.status === "blocked" && peer.direction === "outgoing";

  /** Run block/unblock and apply the SERVER-returned view to `chatPeer` (the
   * single relationship source this screen reads), never an optimistic flip. */
  const mutateBlock = async (fn: (id: string) => Promise<FriendView>): Promise<void> => {
    setBlockBusy(true);
    setBlockError(null);
    try {
      setChatPeer(await fn(peer.userId));
    } catch (e) {
      setBlockError(faApiError(e));
    } finally {
      setBlockBusy(false);
    }
  };

  if (status === "error" || status === "failed") {
    // `error` — the typed `dm-error` path: `BLOCKED` (a block in either direction
    // refused the send — typically the peer blocked YOU) gets its real reason.
    // `failed` — the reconnect cycle exhausted its attempts (or the initial join
    // failed): the SAME dead-end, plus «تلاش دوباره» to restart the backoff cycle.
    const reason =
      status === "error" && error === DM_BLOCKED_CODE ? fa.chat.blockedSendError : fa.chat.error;
    return (
      <div
        className="grid h-full place-items-center bg-surface p-6 text-center"
        style={{ backgroundImage: FELT }}
      >
        <div className="flex flex-col items-center gap-3">
          <p className="m-0 font-body-md text-body-md text-error">{reason}</p>
          {status === "failed" ? (
            <button
              type="button"
              onClick={retry}
              className="cursor-pointer rounded-full border-0 bg-secondary px-5 py-2.5 font-body-lg text-body-lg text-ink-on-gold shadow-md transition-transform active:scale-95"
            >
              {fa.chat.retry}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setChatPeer(null)}
            className={
              status === "failed"
                ? "cursor-pointer rounded-full border border-outline-variant bg-surface-container-high px-5 py-2.5 font-body-lg text-body-lg text-on-surface-variant transition-transform active:scale-95"
                : "cursor-pointer rounded-full border-0 bg-secondary px-5 py-2.5 font-body-lg text-body-lg text-ink-on-gold shadow-md transition-transform active:scale-95"
            }
          >
            {fa.chat.back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ChatThreadView
        title={displayName(peer)}
        official={peer.isOfficial ?? false}
        avatarId={peer.avatarId}
        avatarPhotoId={peer.avatarPhotoId}
        selfId={selfId}
        messages={messages}
        // The thread is «connecting» only while it has NOTHING to show. Once the HTTP page has
        // landed the conversation is readable, even if the socket is still coming up — which is
        // the entire point of painting from HTTP.
        connecting={loadingHistory && messages.length === 0}
        reconnecting={status === "reconnecting"}
        hasMore={hasMore}
        loadingOlder={loadingOlder}
        onLoadOlder={loadOlder}
        onSend={send}
        onSendVoice={(ref) => sendVoice(ref.voiceId, ref.durationMs)}
        onBack={() => setChatPeer(null)}
        onOpenProfile={() => openUserProfile(peer.userId)}
        blockedByMe={blockedByMe}
        blockBusy={blockBusy}
        onToggleBlock={() => {
          // Both directions confirm — see `confirmingUnblock`.
          if (blockedByMe) setConfirmingUnblock(true);
          else setConfirmingBlock(true);
        }}
      />
      {confirmingUnblock ? (
        <ConfirmDialog
          title={fa.blockedUsers.unblockConfirmTitle}
          body={fa.blockedUsers.unblockConfirmBody(displayName(peer))}
          confirmLabel={fa.blockedUsers.unblockConfirm}
          cancelLabel={fa.blockedUsers.unblockCancel}
          danger={false}
          onConfirm={() => {
            setConfirmingUnblock(false);
            void mutateBlock(unblockUser);
          }}
          onCancel={() => setConfirmingUnblock(false)}
        />
      ) : null}
      {confirmingBlock ? (
        <ConfirmDialog
          title={fa.chat.blockConfirmTitle}
          body={fa.chat.blockConfirmBody(displayName(peer))}
          confirmLabel={fa.chat.blockConfirm}
          cancelLabel={fa.chat.blockCancel}
          onConfirm={() => {
            setConfirmingBlock(false);
            void mutateBlock(blockUser);
          }}
          onCancel={() => setConfirmingBlock(false)}
        />
      ) : null}
      {blockError ? (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-24 z-50 mx-auto w-max max-w-[90%] rounded-full bg-error px-4 py-2 font-body-md text-body-md text-on-error shadow-lg"
        >
          {blockError}
        </div>
      ) : null}
    </>
  );
}

/** The chat tab: a conversation (friend) picker with unread badges, or an open
 * DM thread. Opening a thread clears its badge (store + server-persisted). */
export function Chat(): ReactNode {
  const profile = useSession((s) => s.profile);
  const chatPeer = useSession((s) => s.chatPeer);
  const openChat = useSession((s) => s.openChat);
  const setView = useSession((s) => s.setView);
  const dmUnread = useSession((s) => s.dmUnread);
  const dmPreviews = useSession((s) => s.dmPreviews);
  const dmPeers = useSession((s) => s.dmPeers);
  const dmBlocked = useSession((s) => s.dmBlocked);
  const refreshDmUnread = useSession((s) => s.refreshDmUnread);
  const hideDmThread = useSession((s) => s.hideDmThread);
  // The conversation pending one-sided delete (its confirm dialog is open).
  const [deletingPeer, setDeletingPeer] = useState<FriendView | null>(null);
  /** «گفتگوی جدید» — the friend picker sheet. */
  const [picking, setPicking] = useState(false);
  /** How many conversation rows are rendered (see DM_LIST_PAGE). */
  const [shownRows, setShownRows] = useState(DM_LIST_PAGE);
  /** The conversations search is opt-IN now — it no longer occupies the list by default. */
  const [searchOpen, setSearchOpen] = useState(false);

  // Read the friends list from the STORE (kept live-fresh by pushes + the poll) rather than a
  // one-shot local fetch — so a friend accepted/removed while the picker is open reflects here
  // too. `null` = the first load is still in flight → the Spinner (never flash the "no friends
  // yet" empty state at somebody who has friends on a slow link).
  const storeFriends = useSession((s) => s.friends);
  const refreshFriends = useSession((s) => s.refreshFriends);
  const [everLoaded, setEverLoaded] = useState(storeFriends.length > 0);
  const [query, setQuery] = useState("");
  const friends: FriendView[] | null = everLoaded || storeFriends.length > 0 ? storeFriends : null;

  useEffect(() => {
    // Focus refresh: the picker mounts only while چت is the active view, so the friends list AND
    // the unread counts are server-fresh whenever the user lands here.
    void refreshFriends().finally(() => setEverLoaded(true));
    // …AND THE THREAD LIST ITSELF. `dm-summary` is not only the unread badge: `dmPreviews` and
    // `dmPeers` — the last-message line and the name on every row of THIS screen — come from it,
    // and nothing here refreshed them. The 20 s poll did it invisibly; with the poll down to a
    // safety net, the screen that renders that data has to ask for it when it opens. Conditional,
    // so an unchanged list costs a 304 with no body.
    void refreshDmUnread();
  }, [refreshFriends, refreshDmUnread]);

  useEffect(() => {
    // On mount AND every return from an open thread: re-pull the summaries so
    // the row the user just chatted in previews the newest message.
    if (!chatPeer) void refreshDmUnread();
  }, [chatPeer, refreshDmUnread]);

  if (chatPeer && profile) {
    // `selfId` is the caller's app user id (cuid) — the DM room + DB key on it,
    // and it marks own bubbles (a message's `senderId` is now the app id too).
    return <ChatThread peer={chatPeer} selfId={profile.id} />;
  }

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const loaded = friends ?? [];
  const friendById = new Map(loaded.map((f) => [f.userId, f]));
  /**
   * THE ROW SET COMES FROM `/me/dm-summary`, NOT FROM THE FRIENDS LIST.
   *
   * It used to be `friends.filter(hasConversation)` — so a conversation could only be drawn if its
   * peer happened to be an accepted friend, and UNFRIENDING SOMEBODY DELETED THE WHOLE THREAD FROM
   * THE SCREEN. The server never stopped serving it: measured on prod 2026-08-25, `/friends/all`
   * answered 42 bytes («no friends») in the same second `/me/dm-summary` answered 155 bytes («one
   * conversation»), for 1,201 conversations / 53,973 messages / 781 users. Users read that as
   * «چت ها باگ میخوره… کلا حذف میشه» and hunted their partner through search to get the chat back.
   *
   * The friends list is still consulted — but only for the RELATIONSHIP (status/direction/
   * following), which the summary deliberately does not carry. A peer who is a friend keeps their
   * live entry so «مسدود کردن»/«آنبلاک» stay correct; anyone else is rendered from the summary's
   * leak-free identity with no relationship at all.
   *
   * ⚠️ A BLOCKED PAIR DOES REACH HERE NOW, AND `dmBlocked` IS THE ONLY THING THAT SAYS SO. The api
   * used to omit the thread from the summary entirely — which meant the person who had been
   * blocked watched a conversation with all its history vanish from their list, and that says as
   * much as an error message does. Both sides keep the row; only the BLOCKER's is marked.
   *
   * The marker cannot be derived here: a blocked peer is in none of `/friends/all`'s three
   * arrays, so their row would read `status: "none"` and the thread would open with a live
   * composer over a send the server refuses. Stamping `blocked/outgoing` on it is what lights up
   * the muted row below AND `ChatThread`'s `blockedByMe`, with no new prop threaded anywhere.
   */
  const conversationPeers: FriendView[] = [
    ...new Set([...Object.keys(dmPreviews), ...Object.keys(dmUnread)]),
  ].flatMap((userId) => {
    const blocked = dmBlocked[userId]
      ? ({ status: "blocked", direction: "outgoing" } as const)
      : null;
    const friend = friendById.get(userId);
    if (friend) return [blocked ? { ...friend, ...blocked } : friend];
    const peer = dmPeers[userId];
    // No identity and not a friend ⇒ an older api that sends no `peer`. Skip rather than draw an
    // unnameable row: that is exactly today's behaviour, so a stale api never regresses anything.
    return peer
      ? [
          {
            ...peer,
            status: "none",
            direction: "none",
            following: false,
            ...blocked,
          } as FriendView,
        ]
      : [];
  });
  const conversations = conversationPeers
    // ISO timestamps sort lexicographically → most-recently-active conversation first.
    .sort((a, b) =>
      (dmPreviews[b.userId]?.sentAt ?? "").localeCompare(dmPreviews[a.userId]?.sentAt ?? ""),
    );
  // SEARCH widens to every friend AND every conversation peer (matching the label the user sees +
  // the @handle), so you can start a new chat by name and still find a thread with a non-friend.
  const searchable = [
    ...conversationPeers,
    ...loaded.filter((f) => !dmPreviews[f.userId] && !(dmUnread[f.userId] ?? 0)),
  ];
  const filtered = searching
    ? searchable.filter(
        (u) =>
          displayName(u).toLowerCase().includes(q) ||
          (u.appUsername ?? "").toLowerCase().includes(q),
      )
    : conversations;
  // Searching shows its matches from the top; browsing pages down through the list.
  const visibleRows = searching ? filtered.slice(0, DM_LIST_PAGE) : filtered.slice(0, shownRows);
  const hiddenRows = filtered.length - visibleRows.length;

  return (
    <div
      className="flex min-h-full flex-col bg-surface"
      style={{ backgroundImage: FELT }}
      dir="rtl"
    >
      <main className="mx-auto flex w-full max-w-container-max flex-1 flex-col px-md pb-6 pt-md">
        {/* Page header */}
        <div className="mb-4 mt-1 flex items-center justify-between">
          <ScreenTitle icon="chat" title={fa.chat.title} />
          {/* ⚠️ This used to `setView("friends")` — and a friend row THERE opens the peer's
              profile, which had no message action. So the one button meant for «start a
              conversation» could not start one; searching by name was the only way. It now opens
              the friend picker directly. */}
          <button
            type="button"
            aria-label={t.compose}
            onClick={() => setPicking(true)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-outline-variant bg-surface-container-high text-secondary shadow-sm transition-transform hover:bg-surface-bright/20 active:scale-95"
          >
            <ComposeIcon />
          </button>
        </div>

        {friends === null && conversations.length === 0 ? (
          /* First load in flight — the shared loading affordance, never a
             flash of the wrong empty state. */
          <Spinner />
        ) : loaded.length === 0 && conversations.length === 0 ? (
          /* No friends AND no conversations. Having a conversation is now enough to render the
             list, so this state must no longer be decided by the friends list alone — that is
             precisely what hid 781 users' threads behind «هنوز دوستی نداری». */
          /* Empty state — no friends yet: invite / add a friend to start a chat. */
          <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-outline-variant bg-surface-container-low/50 p-6 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-high text-secondary">
              <GroupAddIcon />
            </span>
            <p className="m-0 font-body-md text-body-md text-on-surface-variant">
              {fa.chat.noFriends}
            </p>
            <button
              type="button"
              onClick={() => setView("friends")}
              className="mt-4 cursor-pointer rounded-full border-0 bg-primary-container px-6 py-2 font-body-lg text-body-lg text-on-primary-container shadow-md transition-transform active:scale-95"
            >
              {fa.friends.add}
            </button>
          </div>
        ) : (
          <>
            {/* SEARCH IS OPT-IN. A permanently-mounted search box above three conversations is
                a box you scroll past, not a feature; and finding a person to START a chat with is
                the picker's job now, not this field's. It stays for filtering a long list, one tap
                away, and auto-shows once the list is genuinely long enough to need it. */}
            {searchOpen || conversations.length > DM_LIST_PAGE ? (
              <div className="relative mb-4">
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-outline">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  autoFocus={searchOpen}
                  aria-label={t.search}
                  placeholder={t.searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-surface-container-highest bg-surface-container-low py-2.5 pl-4 pr-12 font-body-md text-body-md text-on-surface outline-none transition-colors placeholder:text-outline-variant focus:border-secondary"
                  style={{ boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)" }}
                />
              </div>
            ) : conversations.length > 0 ? (
              <button
                type="button"
                aria-label={fa.chat.searchConversations}
                onClick={() => setSearchOpen(true)}
                className="mb-3 flex cursor-pointer appearance-none items-center gap-1.5 self-start rounded-full border border-outline-variant/60 bg-transparent px-3 py-1.5 font-caption text-caption text-on-surface-variant active:scale-95"
              >
                <SearchIcon />
                {fa.chat.searchConversations}
              </button>
            ) : null}

            {filtered.length === 0 ? (
              searching ? (
                <p className="m-0 mt-2 px-1 font-body-md text-body-md text-on-surface-variant">
                  {fa.friends.noResults}
                </p>
              ) : (
                /* Has friends, but no conversation started yet — NOT the "no friends" state.
                   Search a friend or tap ✎ (→ Friends) to begin. */
                <div className="mt-6 flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-outline-variant bg-surface-container-low/50 p-6 text-center">
                  <p className="m-0 font-body-md text-body-md text-on-surface">
                    {t.noConversations}
                  </p>
                  <p className="m-0 font-caption text-caption text-on-surface-variant">
                    {t.noConversationsHint}
                  </p>
                </div>
              )
            ) : (
              <>
                {/* The «pick a friend to start» hint belongs to the SEARCH (start-new) view; the
                    default view is the existing-conversations list and needs no such prompt. */}
                {searching ? (
                  <p className="m-0 mb-2 px-1 font-caption text-caption text-on-surface-variant">
                    {fa.chat.pickFriend}
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  {visibleRows.map((u, i) => (
                    <div
                      key={u.userId}
                      className="game-grid-in"
                      style={{ animationDelay: `${Math.min(i, 8) * 24}ms` }}
                    >
                      <ConversationRow
                        user={u}
                        unread={dmUnread[u.userId] ?? 0}
                        preview={dmPreviews[u.userId] ?? null}
                        onOpen={() => openChat(u)}
                        onDelete={() => setDeletingPeer(u)}
                      />
                    </div>
                  ))}
                  {/* Bounded list + «بیشتر». A SCALE GUARD: the busiest account today has 17
                      conversations, so nobody reaches this — but the list is built from every
                      friend joined to the summary, and «render them all» is fine right up until
                      it is not. */}
                  {hiddenRows > 0 ? (
                    <button
                      type="button"
                      onClick={() => setShownRows((n) => n + DM_LIST_PAGE)}
                      className="mt-1 w-full cursor-pointer appearance-none rounded-xl border border-outline-variant/60 bg-transparent py-2.5 font-body-md text-body-md text-secondary active:scale-[0.99]"
                    >
                      {fa.chat.moreConversations}
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </>
        )}
      </main>
      {picking ? (
        <FriendPickerSheet
          friends={loaded}
          onClose={() => setPicking(false)}
          onPick={(friend) => {
            setPicking(false);
            openChat(friend);
          }}
        />
      ) : null}
      {deletingPeer ? (
        <ConfirmDialog
          title={fa.chat.deleteThread}
          body={fa.chat.deleteThreadBody}
          confirmLabel={fa.chat.deleteThreadConfirm}
          cancelLabel={fa.chat.deleteThreadCancel}
          onConfirm={() => {
            void hideDmThread(deletingPeer.userId);
            setDeletingPeer(null);
          }}
          onCancel={() => setDeletingPeer(null)}
        />
      ) : null}
    </div>
  );
}
